<template>
  <!-- Contenedores por posición -->
  <div v-for="position in positions" :key="position" :class="getContainerClasses(position)">
    <transition-group name="notification" tag="div" class="space-y-3">
      <div
        v-for="notification in getNotificationsByPosition(position)"
        :key="notification.id"
        :class="getNotificationClasses(notification)"
      >
        <div :class="getContentClasses(notification)">
          <div class="flex items-center">
            <!-- Icon -->
            <div class="flex-shrink-0 mr-3">
              <div :class="getIconClasses(notification.type)">
                <!-- Success Icon -->
                <svg v-if="notification.type === 'success'" class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <!-- Error Icon -->
                <svg v-else-if="notification.type === 'error'" class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                <!-- Warning Icon -->
                <svg v-else-if="notification.type === 'warning'" class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                <!-- Info Icon -->
                <svg v-else class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
            
            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <p :class="getTitleClasses(notification)">
                  {{ notification.title }}
                </p>
                <!-- Close button -->
                <button
                  @click="removeNotification(notification.id)"
                  class="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-500 rounded-md p-1 transition-colors"
                >
                  <span class="sr-only">Cerrar</span>
                  <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
              <p v-if="notification.message" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {{ notification.message }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Progress bar (solo si no es compacta) -->
        <div v-if="!notification.options?.compact" class="h-1 bg-gray-200 dark:bg-gray-700 rounded-b-lg overflow-hidden">
          <div 
            :class="[
              'h-full transition-all duration-[4000ms] ease-linear',
              getProgressBarClass(notification.type)
            ]"
            style="width: 0%; animation: progress 4s linear forwards;"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { useNotifications } from '@/composables/useNotifications'

const { notifications, removeNotification } = useNotifications()

// Posiciones disponibles
const positions = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right']

// Obtener notificaciones por posición
const getNotificationsByPosition = (position) => {
  return notifications.filter(n => n.options?.position === position || (position === 'top-right' && !n.options?.position))
}

// Clases del contenedor según posición
const getContainerClasses = (position) => {
  const baseClasses = 'fixed z-50 pointer-events-none'
  const positions = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4'
  }
  return `${baseClasses} ${positions[position]}`
}

// Clases de la notificación
const getNotificationClasses = (notification) => {
  const compact = notification.options?.compact
  return [
    'pointer-events-auto transform transition-all duration-300 hover:scale-105',
    'bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700',
    'rounded-lg overflow-hidden',
    compact ? 'max-w-xs' : 'max-w-sm w-full',
    'backdrop-blur-sm'
  ]
}

// Clases del contenido
const getContentClasses = (notification) => {
  const compact = notification.options?.compact
  return compact ? 'px-4 py-3' : 'px-4 py-3'
}

// Clases del título
const getTitleClasses = (notification) => {
  const compact = notification.options?.compact
  return [
    'font-medium text-gray-900 dark:text-white',
    compact ? 'text-sm' : 'text-sm',
    'truncate'
  ]
}

// Clases del ícono
const getIconClasses = (type) => {
  const colors = {
    success: 'text-green-500 bg-green-100 dark:bg-green-900/30',
    error: 'text-red-500 bg-red-100 dark:bg-red-900/30',
    warning: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30',
    info: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30'
  }
  return `${colors[type]} rounded-full p-1.5`
}

// Clases de la barra de progreso
const getProgressBarClass = (type) => {
  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  }
  return colors[type]
}
</script>

<style scoped>
/* Animaciones para las transiciones */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}

/* Animación de la barra de progreso */
@keyframes progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}
</style>